import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { VariantProps, tv } from 'tailwind-variants';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';

const pillStyle = tv({
  slots: {
    container: ['px-2 py-1 rounded-md border w-fit flex items-center gap-1 text-base', ' font-semibold'],
    description: ['text-xs font-normal mt-1'],
    startIcon: ['mt-[1px]'],
  },

  variants: {
    variant: {
      success: {
        container: ['bg-success-100 text-success-700 border-success-400'],
        icon: ['text-success-400'],
        description: ['text-success-600'],
      },
      warning: {
        container: ['bg-warning-100 text-warning-700 border-warning-400'],
        icon: ['text-warning-500'],
      },
      danger: {
        container: ['bg-danger-100 text-danger-700 border-danger-400'],
        icon: ['text-danger-600'],
      },
      info: {
        container: ['bg-blue-100 text-blue-600 border-blue-600'],
        icon: ['text-blue-600'],
      },
      light: {
        container: ['bg-gray-100 text-gray-700 border-gray-400'],
        icon: ['text-gray-400'],
      },
      search: {
        container: ['bg-pink-100 border-pink-100 text-pink-700'],
        icon: ['text-pink-700'],
      },
    },
    hasDescription: {
      true: {},
    },
  },

  compoundVariants: [
    {
      hasDescription: true,
      className: {
        container: 'p-3 gap-3',
      },
    },
  ],

  defaultVariants: {
    variant: 'success',
  },
});

export type VariantsPillProps = VariantProps<typeof pillStyle>;

export interface DecorativePillProps extends Omit<VariantsPillProps, 'hasDescription'>, React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  icon?: IconDefinition;
  label?: string;
  description?: string;
  className?: string;
  iconSize?: SizeProp;
  children?: React.ReactNode;
}

export function DecorativePill({
  label,
  description,
  variant,
  icon: startIcon,
  className,
  iconSize: startIconSize,
  ...rest
}: DecorativePillProps) {
  const hasDescription = Boolean(description);
  const dataTestId = rest['data-testid'];

  const { container, description: descriptionStyle, startIcon: startIconStyle } = pillStyle({ variant, hasDescription });

  return (
    <div className={container({ className })} data-testid={dataTestId ?? DataTestIdEnum.DECORATIVE_PILL_CONTAINER} {...rest}>
      {startIcon && (
        <Icon
          data-testid={DataTestIdEnum.DECORATIVE_PILL_START_ICON}
          icon={startIcon}
          size={startIconSize || 'sm'}
          className={startIconStyle()}
        />
      )}

      <div className="flex justify-content truncate">
        <span data-testid={DataTestIdEnum.DECORATIVE_PILL_LABEL} className="truncate">
          {label}
        </span>
        {hasDescription && (
          <p className={descriptionStyle()} data-testid={DataTestIdEnum.DECORATIVE_PILL_DESCRIPTION}>
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
